import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";

export function useLogin() {
  return useMutation({
    mutationFn: async ({
      email,
      password,
    }: {
      email: string;
      password: string;
    }) => {
      return apiClient.login(email, password);
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => apiClient.getMe(),
    retry: 1,
  });
}

export function useItems(all = false) {
  return useQuery({
    queryKey: ["items", { all }],
    queryFn: () => apiClient.getItems(all),
  });
}

export function useCreateItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, any>) => apiClient.createItem(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["items"] }),
  });
}

export function useUpdateItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      data,
    }: {
      itemId: string;
      data: Record<string, any>;
    }) => apiClient.updateItem(itemId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["items"] }),
  });
}

export function useUsers() {
  return useQuery({
    queryKey: ["users"],
    queryFn: () => apiClient.getUsers(),
  });
}

export function useCreateStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { code: string; name: string }) =>
      apiClient.createStore(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["stores"] }),
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      email: string;
      password: string;
      fullName?: string;
      role: string;
      storeId?: string;
    }) => apiClient.createUser(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useLockedPeriods() {
  return useQuery({
    queryKey: ["locked-periods"],
    queryFn: () => apiClient.getLockedPeriods(),
  });
}

export function useStores() {
  return useQuery({
    queryKey: ["stores"],
    queryFn: () => apiClient.getStores(),
  });
}

export function useDayEntries(storeId: string, date: string) {
  return useQuery({
    queryKey: ["entries", storeId, date],
    queryFn: () => apiClient.getDayEntries(storeId, date),
    enabled: !!storeId && !!date,
  });
}

function applyEntryPatch(entry: Record<string, any>, patch: Record<string, any>) {
  const next = { ...entry, ...patch };
  // Mirror the DB-generated columns so the UI reflects edits instantly.
  const opening = Number(next.opening) || 0;
  const purchases = Number(next.purchases) || 0;
  const sales = Number(next.sales) || 0;
  next.closing = opening + purchases - sales;
  const physical =
    next.physicalCount === null ||
    next.physicalCount === undefined ||
    next.physicalCount === ""
      ? null
      : Number(next.physicalCount);
  next.variance = physical === null ? null : physical - next.closing;
  return next;
}

export function useUpdateEntry() {
  const queryClient = useQueryClient();

  const patchCaches = (entryId: string, patch: Record<string, any>) => {
    queryClient.setQueriesData({ queryKey: ["entries"] }, (old: any) => {
      if (!Array.isArray(old)) return old;
      return old.map((entry: any) =>
        entry.id === entryId ? applyEntryPatch(entry, patch) : entry
      );
    });
  };

  return useMutation({
    mutationFn: async ({
      entryId,
      data,
    }: {
      entryId: string;
      data: Record<string, any>;
    }) => {
      return apiClient.updateEntry(entryId, data);
    },
    onMutate: async ({ entryId, data }) => {
      await queryClient.cancelQueries({ queryKey: ["entries"] });
      const snapshots = queryClient.getQueriesData({ queryKey: ["entries"] });
      patchCaches(entryId, data);
      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      context?.snapshots?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
    },
    onSuccess: (updated, { entryId }) => {
      // Reconcile with the server-computed row (closing/variance are
      // DB-generated) without refetching the whole day.
      if (updated && typeof updated === "object") {
        queryClient.setQueriesData({ queryKey: ["entries"] }, (old: any) => {
          if (!Array.isArray(old)) return old;
          return old.map((entry: any) =>
            entry.id === entryId ? { ...entry, ...updated } : entry
          );
        });
      }
    },
  });
}

export function useMonthEnd(storeId: string, month: string) {
  return useQuery({
    queryKey: ["month-end", storeId, month],
    queryFn: () => apiClient.getMonthEnd(storeId, month),
    enabled: !!storeId && !!month,
  });
}

export function useAdminOverview() {
  return useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => apiClient.getAdminOverview(),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

export function useLockPeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      storeId,
      month,
    }: {
      storeId: string;
      month: string;
    }) => {
      return apiClient.lockPeriod(storeId, month);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locked-periods"] });
    },
  });
}

export function useUnlockPeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      storeId,
      month,
    }: {
      storeId: string;
      month: string;
    }) => {
      return apiClient.unlockPeriod(storeId, month);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locked-periods"] });
    },
  });
}

export function useAudit(params: {
  storeId?: string;
  from?: string;
  to?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ["audit", params],
    queryFn: () => apiClient.getAudit(params),
  });
}

export function useTrendsDaily(days = 30) {
  return useQuery({
    queryKey: ["trends", "daily", days],
    queryFn: () => apiClient.getTrendsDaily(days),
  });
}

export function useTrendsMonthly(months = 6) {
  return useQuery({
    queryKey: ["trends", "monthly", months],
    queryFn: () => apiClient.getTrendsMonthly(months),
  });
}
